import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ArrowRight } from 'lucide-react';
import CountdownTimer from './CountdownTimer';
import { isVideoMedia } from '../utils/media';

// Helper to parse date string and get a target date
const parseRallyDate = (dateString) => {
  // Try to extract the first date from strings like "April 17-19, 2026" or "February 21, 2026"
  const match = dateString.match(/([A-Za-z]+)\s+(\d+)(?:-\d+)?,?\s*(\d{4})/);
  if (match) {
    const [, month, day, year] = match;
    return new Date(`${month} ${day}, ${year}`);
  }
  return null;
};

const RallyCard = ({ rally, index = 0 }) => {
  const targetDate = parseRallyDate(rally.dates);
  const showCountdown = (rally.status === 'open' || rally.status === 'upcoming') && targetDate && targetDate > new Date();

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <span className="badge-open px-3 py-1 text-xs font-['Oswald'] tracking-wider">OPEN</span>;
      case 'completed':
        return <span className="badge-completed px-3 py-1 text-xs font-['Oswald'] tracking-wider">COMPLETED</span>;
      case 'coming_soon':
        return <span className="badge-coming-soon px-3 py-1 text-xs font-['Oswald'] tracking-wider">COMING SOON</span>;
      case 'upcoming':
        return <span className="badge-open px-3 py-1 text-xs font-['Oswald'] tracking-wider">UPCOMING</span>;
      default:
        return null;
    }
  };

  return (
    <motion.div
      data-testid={`rally-card-${rally.id}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="card group overflow-hidden"
    >
      {/* Image / Video */}
      <div className="relative h-48 overflow-hidden">
        <Link to={`/rallies/${rally.id}`} data-testid={`rally-image-link-${rally.id}`}>
          {isVideoMedia(rally.image_url, rally.image_media_type) ? (
            <video
              src={rally.image_url}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              muted
              loop
              autoPlay
              playsInline
              preload="metadata"
            />
          ) : (
            <img
              src={rally.image_url || 'https://images.unsplash.com/photo-1610809589386-9ea41901eb54?w=800'}
              alt={rally.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </Link>
        <div className="absolute top-4 right-4">
          {getStatusBadge(rally.status)}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <Link to={`/rallies/${rally.id}`} data-testid={`rally-title-link-${rally.id}`}>
          <h3 className="font-['Oswald'] text-xl text-[#F2EDE6] mb-3 group-hover:text-[#E8651A] transition-colors">
            {rally.name}
          </h3>
        </Link>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-[#A19D98] text-sm">
            <Calendar size={14} />
            <span>{rally.dates}</span>
          </div>
          {rally.route && (
            <div className="flex items-center gap-2 text-[#A19D98] text-sm">
              <MapPin size={14} />
              <span>{rally.route}</span>
            </div>
          )}
        </div>

        {rally.description && (
          <p className="text-[#A19D98] text-sm mb-4 line-clamp-2">
            {rally.description}
          </p>
        )}

        {rally.includes && rally.includes.length > 0 && (
          <ul className="text-sm text-[#A19D98] mb-4 space-y-1">
            {rally.includes.slice(0, 3).map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-[#E8651A]">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Countdown Timer */}
        {showCountdown && (
          <div className="mb-4 py-4 border-t border-[#2A2A2A]">
            <CountdownTimer targetDate={targetDate} label="Rally starts in" />
          </div>
        )}

        {/* CTA */}
        {rally.status === 'open' || rally.status === 'upcoming' ? (
          <div className="space-y-2">
            <Link
              to={`/rallies/${rally.id}/register`}
              data-testid={`rally-register-btn-${rally.id}`}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              Register for this Rally
              <ArrowRight size={16} />
            </Link>
            <Link
              to={`/rallies/${rally.id}`}
              data-testid={`rally-details-btn-${rally.id}`}
              className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
            >
              View Details & Route
            </Link>
          </div>
        ) : rally.status === 'coming_soon' ? (
          <div className="space-y-2">
            <Link
              to={`/rallies?notify=${rally.id}`}
              data-testid={`rally-notify-btn-${rally.id}`}
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              Notify Me
            </Link>
            <Link
              to={`/rallies/${rally.id}`}
              data-testid={`rally-details-btn-${rally.id}`}
              className="text-[#E8651A] hover:text-[#FF7A2F] text-sm font-['Oswald'] tracking-wider flex items-center justify-center gap-1"
            >
              View Details
              <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <Link
            to={`/rallies/${rally.id}`}
            data-testid={`rally-details-btn-${rally.id}`}
            className="btn-secondary w-full flex items-center justify-center gap-2"
          >
            View Recap
            <ArrowRight size={16} />
          </Link>
        )}
      </div>
    </motion.div>
  );
};

export default RallyCard;
